import { OrderQuantitySelector } from "./OrderQuantitySelector";
import type { ShippingAddress } from "@stripe/stripe-js";
import { Elements, AddressElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getContrastTextColor } from "@/utils/colorUtils";
import { shippingAddressWithoutPhone } from "@/lib/shippingAddressWithoutPhone";

interface Product {
  _id: string;
  name?: string;
  price: number;
}

interface CheckoutShippingStepProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  shippingAddress: ShippingAddress | null;
  onShippingAddressChange: (address: ShippingAddress | null) => void;
  onAddressCompleteChange: (complete: boolean) => void;
  stripePromise: ReturnType<typeof loadStripe> | null;
  /** Product / PWYC charge in cents (excludes shipping) */
  total: number;
  shippingCents?: number;
  shippingLabel?: string;
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  checkoutSettings?: {
    colors?: {
      backgroundColor?: string;
      accentColor?: string;
    };
  } | null;
}

export function CheckoutShippingStep({
  products,
  quantities,
  onQuantityChange,
  shippingAddress,
  onShippingAddressChange,
  onAddressCompleteChange,
  stripePromise,
  total,
  shippingCents = 0,
  shippingLabel = "",
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  checkoutSettings,
}: CheckoutShippingStepProps) {
  const backgroundColor = checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const textColorHex = getContrastTextColor(backgroundColor) === "white" ? "#ffffff" : "#1a1a1a";
  const elementsAppearance = {
    variables: {
      colorBackground: backgroundColor,
      colorText: textColorHex,
    },
  };
  const getAddressDefaultValues = () => {
    if (!shippingAddress) return undefined;
    const a = shippingAddress.address;
    return {
      name: shippingAddress.name || "",
      address: {
        line1: a?.line1 ?? "",
        line2: a?.line2 ?? "",
        city: a?.city ?? "",
        state: a?.state ?? "",
        postal_code: a?.postal_code ?? "",
        country: a?.country || "US",
      },
    };
  };

  return (
    <div className="space-y-4 flex-1">
      <OrderQuantitySelector
        products={products}
        quantities={quantities}
        onQuantityChange={onQuantityChange}
        total={total}
        showShippingLineItem={false}
        showShippingProtectionLineItem={false}
        shippingCents={shippingCents}
        shippingLabel={shippingLabel || undefined}
        shippingProtectionCents={shippingProtectionCents}
        shippingProtectionSelectable={shippingProtectionSelectable}
        shippingProtectionUnitCents={shippingProtectionUnitCents}
        shippingProtectionChecked={shippingProtectionChecked}
        onShippingProtectionChange={onShippingProtectionChange}
        checkoutColors={checkoutSettings?.colors ?? null}
      />

      <div className="space-y-4">
        {stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{ appearance: elementsAppearance }}
            key={`address-element-${shippingAddress ? "has-address" : "no-address"}`}
          >
            <AddressElement
              options={{
                mode: "shipping",
                fields: {
                  phone: "never",
                },
                defaultValues: getAddressDefaultValues(),
              }}
              onChange={(event) => {
                const v = event.value;
                const inner = v.address as {
                  line1?: string;
                  line2?: string;
                  city?: string;
                  state?: string;
                  country?: string;
                  postal_code?: string;
                } | null;
                if (inner && typeof inner === "object") {
                  const built: ShippingAddress = {
                    name: v.name ?? "",
                    address: {
                      line1: inner.line1 ?? "",
                      line2: inner.line2,
                      city: inner.city ?? "",
                      state: inner.state ?? "",
                      country: inner.country ?? "",
                      postal_code: inner.postal_code ?? "",
                    },
                  };
                  onShippingAddressChange(shippingAddressWithoutPhone(built, v.name));
                }
                onAddressCompleteChange(event.complete);
              }}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}
